import { useMemo, useState } from 'react';
import { Box, Button, Typography, Link, TextField } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import api from '../api/api';
import BackgroundImage from '../assets/img/main-bg-pg.svg';
import LoginImage from '../assets/img/login-page-img.svg';
import '../assets/style/LoginScreen.css';

const LogoIcon = (props) => (
  <svg fill="none" height="100%" viewBox="0 0 24 24" width="100%" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path opacity="0.16" d="M7.242 11.083c.449-1.674 2.17-3.394 3.843-3.843l10.434-2.796c1.673-.448 2.666.545 2.218 2.218L20.94 17.096c-.449 1.674-2.17 3.394-3.843 3.843L6.664 23.735c-1.673.448-2.666-.545-2.218-2.218l2.796-10.434Z" fill="#6366F1"></path>
    <path d="M3.06 6.9c.448-1.674 2.168-3.394 3.842-3.843L17.336.261c1.673-.448 2.667.545 2.218 2.218l-2.796 10.434c-.449 1.674-2.169 3.394-3.843 3.843L2.481 19.552C.808 20-.185 19.007.263 17.334L3.06 6.9Z" fill="#6366F1"></path>
  </svg>
);

const LottieAnimation = () => (
  <Box sx={{ width: '70%', margin: '0px auto' }}>
    <img src={LoginImage} alt="" style={{ width: '100%' }} />
  </Box>
);

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token') || '';
  const emailFromUrl = searchParams.get('email') || '';

  const [email, setEmail] = useState(emailFromUrl);
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isSubmitDisabled = useMemo(() => {
    return !email || !password || !passwordConfirmation || !tokenFromUrl;
  }, [email, password, passwordConfirmation, tokenFromUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (password !== passwordConfirmation) {
      setError('Passwords do not match.');
      return;
    }
    if (!tokenFromUrl) {
      setError('Reset token is missing. Please use the link from your email.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        email,
        token: tokenFromUrl,
        password,
        password_confirmation: passwordConfirmation,
      };
      await api.post('/auth/reset-password', payload);
      setSuccess('Password has been reset. You can now log in.');
      // Небольшая пауза и переход на логин
      setTimeout(() => navigate('/'), 1200);
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.error || 'Failed to reset password.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-screen-container">
      <div className="login-left-panel" style={{ backgroundImage: `url(${BackgroundImage})` }}>
        <div className="login-left-content">
          <Typography variant="h4" className="login-title">
            Welcome to PalladiumWallet
          </Typography>
          <Typography className="login-subtitle">
            Reset your password securely and continue using your account.
          </Typography>
          <LottieAnimation />
        </div>
      </div>

      <div className="login-right-panel">
        <div className="login-form-container">
          <a href="/" className="logo-link">
            <div className="logo-icon-wrapper">
              <LogoIcon />
            </div>
            <Typography variant="h6" className="logo-text">
              PalladiumWallet
            </Typography>
          </a>

          <div className="login-header">
            <Typography variant="h5" className="login-header-title">
              Reset password
            </Typography>
            <Typography className="login-header-subtitle">
              Enter your email and choose a new password.
            </Typography>
          </div>

          <form noValidate className="login-form" onSubmit={handleSubmit}>
            <div className="form-fields">
              <TextField
                label="Email Address"
                variant="outlined"
                className="login-textfield"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <TextField
                label="Password"
                type="password"
                variant="outlined"
                className="login-textfield"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <TextField
                label="Confirm Password"
                type="password"
                variant="outlined"
                className="login-textfield"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                required
              />
            </div>

            {tokenFromUrl === '' && (
              <Typography className="error-message">Reset token is missing.</Typography>
            )}
            {error && <Typography className="error-message">{error}</Typography>}
            {success && <Typography className="success-message">{success}</Typography>}

            <div className="form-spacer" />
            <Button
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              className="continue-button"
              loading={loading}
              disabled={isSubmitDisabled}
            >
              Reset password
            </Button>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link component="button" onClick={() => navigate('/')} className="register-link">
                Back to login
              </Link>
            </Box>
          </form>
        </div>
      </div>
    </div>
  );
}
